import React, { useEffect, useState, useRef, useCallback } from "react";
import { GrPowerReset } from "react-icons/gr";
import { Helmet } from "react-helmet-async";

function ResetIconBtn({ onClick, size = 16, topOffset = "12px" }) {
  const [hover, setHover] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      aria-label="Clear"
      style={{
        position: "absolute",
        right: "8px",
        top: "50%",
        transform: "translateY(-50%)",
        background: "transparent",
        border: "none",
        cursor: "pointer",
        fontSize: `${size}px`,
        color: hover ? "#333" : "#999",
        padding: 0,
        width: `${size}px`,
        height: `${size}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginTop: topOffset,
        transition: "color 0.2s ease-in-out",
      }}
    >
      <GrPowerReset style={{ pointerEvents: "none" }} />
    </button>
  );
}

// 🔒 Password Modal Component
function PasswordModal({
  isOpen,
  onClose,
  onSubmit,
  loading,
  error,
  exportType,
  showBatchButtons = false,
  batchInfo = null,
  downloadingBatch = null
}) {
  const [password, setPassword] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setPassword("");
    }
  }, [isOpen]);

  const handleSubmit = (e, batchPage = null) => {
    e.preventDefault();
    if (password.trim()) {
      onSubmit(password, batchPage);
    }
  };

  const handleClose = () => {
    setPassword("");
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="password-modal" onClick={handleClose}>
      <div
        className="password-modal__content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: showBatchButtons && batchInfo ? "600px" : "400px",
          width: showBatchButtons && batchInfo ? "90%" : "auto",
        }}
      >
        <h2 className="password-modal__title">Export {exportType}</h2>
        <p className="password-modal__description">
          {showBatchButtons
            ? "Enter password and select batch to download"
            : "Enter password to download CSV file"}
        </p>

        <form className="password-modal__form" onSubmit={(e) => handleSubmit(e)}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
            autoFocus
            disabled={loading}
            className="password-modal__input"
          />

          {/* Error Message */}
          {error && <div className="password-modal__error">{error}</div>}

          <div
            className="password-modal__actions"
            style={{
              flexWrap: showBatchButtons && batchInfo ? "wrap" : "nowrap",
              gap: "10px",
            }}
          >
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="password-modal__button password-modal__button--cancel"
              style={{
                flex: showBatchButtons && batchInfo ? "0 0 100%" : "1",
              }}
            >
              Cancel
            </button>

            {/* Show batch buttons or single download button */}
            {showBatchButtons && batchInfo ? (
              <>
                {Array.from({ length: batchInfo.numBatches }, (_, index) => {
                  const pageNum = index + 1;
                  const startEntry = index * batchInfo.batchSize + 1;
                  const endEntry = Math.min((index + 1) * batchInfo.batchSize, batchInfo.totalEntries);
                  const isDownloading = downloadingBatch === pageNum;
                  const isAnyDownloading = loading;

                  return (
                    <button
                      key={`batch-${pageNum}`}
                      type="button"
                      onClick={(e) => handleSubmit(e, pageNum)}
                      disabled={isAnyDownloading || !password.trim()}
                      className="password-modal__button password-modal__button--submit"
                      style={{
                        flex: "1 1 auto",
                        minWidth: "150px",
                        margin: 0,
                      }}
                    >
                      {isDownloading ? "Downloading..." : `Batch ${pageNum} (${startEntry}-${endEntry})`}
                    </button>
                  );
                })}
              </>
            ) : (
              <button
                type="submit"
                disabled={loading || !password.trim()}
                className="password-modal__button password-modal__button--submit"
              >
                {loading ? "Downloading..." : "Download"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

const maskFirstSeven = (mobileNumber) => {
  if (!mobileNumber || mobileNumber.length < 10) return mobileNumber;
  return "xxxxxxx" + mobileNumber.slice(-3);
};

function useDebounce(callback, delay) {
  const timer = useRef();
  return (...args) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      callback(...args);
    }, delay);
  };
}

export default function AdminDashboard() {
  const API_URL = process.env.REACT_APP_API_URL;

  const [activeTab, setActiveTab] = useState("retailers");

  const [retailers, setRetailers] = useState([]);
  const [retailersLoading, setRetailersLoading] = useState(false);
  const [retailersPage, setRetailersPage] = useState(1);
  const [retailersTotal, setRetailersTotal] = useState(0);
  const [retailersSearch, setRetailersSearch] = useState("");
  const [retailersStartDate, setRetailersStartDate] = useState("");
  const [retailersEndDate, setRetailersEndDate] = useState("");

  const [consumers, setConsumers] = useState([]);
  const [consumersLoading, setConsumersLoading] = useState(false);
  const [consumersPage, setConsumersPage] = useState(1);
  const [consumersTotal, setConsumersTotal] = useState(0);
  const [consumersSearch, setConsumersSearch] = useState("");
  const [consumersStartDate, setConsumersStartDate] = useState("");
  const [consumersEndDate, setConsumersEndDate] = useState("");
  const [retailersSortBy, setRetailersSortBy] = useState("updatedDate");
  const [retailersSortOrder, setRetailersSortOrder] = useState("desc");
  const [retailerCounts, setRetailerCounts] = useState(null);
  const [consumerCounts, setConsumerCounts] = useState(null);
  const [retailerCashbacks, setRetailerCashbacks] = useState(null);
  const [consumerCashbacks, setConsumerCashbacks] = useState(null);

  // const [screenCounts, setScreenCounts] = useState([]);
  // const [screenCountsLoading, setScreenCountsLoading] = useState(false);
  const [screenStartDate, setScreenStartDate] = useState("");
  const [screenEndDate, setScreenEndDate] = useState("");
  const [screenCounts, setScreenCounts] = useState([]);
  const [screenCountsLoading, setScreenCountsLoading] = useState(false);

  //

  const [loading, setLoading] = useState(true);
  //
  const [cashbacks, setCashbacks] = useState([]);
  const [cashbacksPage, setCashbacksPage] = useState(1);
  const [cashbacksPageSize] = useState(20);
  const [cashbacksTotalPages, setCashbacksTotalPages] = useState(1);

  // 🔒 Password Modal State
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [exportType, setExportType] = useState(""); // "retailers" or "consumers"
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState("");
  const [showBatchButtons, setShowBatchButtons] = useState(false);
  const [batchInfo, setBatchInfo] = useState(null);
  const [downloadingBatch, setDownloadingBatch] = useState(null); // Track which batch is currently downloading

  const pageSize = 20;

  const handleSort = (field) => {
    if (retailersSortBy === field) {
      setRetailersSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
    } else {
      setRetailersSortBy(field);
      setRetailersSortOrder("asc");
    }
  };

  const fetchRetailerCashgram = () => {
    fetch(`${process.env.REACT_APP_API_URL}retailers/cashgram/count`, {
      cache: "no-store",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.status === "success" && data?.data) {
          setRetailerCashbacks(data?.data);
        }
      })
      .catch((err) => console.error("Retailer cashgram fetch error:", err));
  };

  const fetchRetailerStats = () => {
    fetch(`${process.env.REACT_APP_API_URL}retailers/count`, {
      cache: "no-store",
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.status === "success" && data?.data) {
          setRetailerCounts(data?.data);
        }
      })
      .catch((err) => console.error("Retailer count fetch error:", err));
  };

  const fetchTotalCashback = () => {
    fetch(`${process.env.REACT_APP_API_URL}consumers/cashgram/count`, {
      cache: "no-store",
    })
      .then((res) => res.json())
      .then((data) => {
        setConsumerCashbacks(data?.data);
      })
      .catch((err) => console.error("Cashback count fetch error:", err));
  };

  const fetchTotalEntries = () => {
    fetch(`${process.env.REACT_APP_API_URL}consumers/count`, {
      cache: "no-store",
    })
      .then((res) => res.json())
      .then((data) => {
        setConsumerCounts(data?.data);
      })
      .catch((err) => console.error("Total count fetch error:", err));
  };

  useEffect(() => {
    fetchTotalEntries();
    fetchTotalCashback();
    fetchRetailerStats();
    fetchRetailerCashgram();

    const interval = setInterval(() => {
      fetchTotalEntries();
      fetchTotalCashback();
      fetchRetailerStats();
      fetchRetailerCashgram();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchRetailers = useCallback(() => {
    setRetailersLoading(true);
    const params = new URLSearchParams({
      page: retailersPage,
      pageSize,
      sortBy: retailersSortBy,
      sortOrder: retailersSortOrder,
      search: retailersSearch,
    });
    if (retailersStartDate) params.append("startDate", retailersStartDate);
    if (retailersEndDate) params.append("endDate", retailersEndDate);
    fetch(`${API_URL}retailers?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setRetailers(data.data?.data || []);
          setRetailersTotal(data.data?.meta?.totalItems || 0);
        }
      })
      .catch(console.error)
      .finally(() => setRetailersLoading(false));
  }, [retailersPage, retailersSortBy, retailersSortOrder, retailersSearch, retailersStartDate, retailersEndDate, API_URL]);

  const fetchConsumers = useCallback(() => {
    setConsumersLoading(true);
    const params = new URLSearchParams({
      page: consumersPage,
      pageSize,
    });
    if (consumersSearch) params.append("search", consumersSearch);
    if (consumersStartDate) params.append("startDate", consumersStartDate);
    if (consumersEndDate) params.append("endDate", consumersEndDate);

    fetch(`${API_URL}consumers?${params.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          setConsumers(data.data?.data || []);
          setConsumersTotal(data.data?.meta?.totalItems || 0);
        }
      })
      .catch(console.error)
      .finally(() => setConsumersLoading(false));
  }, [consumersPage, consumersSearch, consumersStartDate, consumersEndDate, API_URL]);

  const debouncedFetchRetailers = useDebounce(fetchRetailers, 500);
  const debouncedFetchConsumers = useDebounce(fetchConsumers, 500);

  useEffect(() => {
    if (activeTab === "retailers") {
      setRetailersLoading(true);
      debouncedFetchRetailers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retailersSearch, retailersPage, retailersSortBy, retailersSortOrder, retailersStartDate, retailersEndDate, activeTab]);

  useEffect(() => {
    if (activeTab === "consumers") {
      setConsumersLoading(true);
      debouncedFetchConsumers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumersSearch, consumersPage, consumersStartDate, consumersEndDate, activeTab]);

  // 🔒 Password Protection Functions
  const openExportModal = (type) => {
    setExportType(type);
    setExportError("");

    // Check if we should show batch buttons for consumers without date filters
    if (type === "consumers" && !consumersStartDate && !consumersEndDate) {
      const totalEntries = consumersTotal || 0;
      const batchSize = 100000;
      const numBatches = Math.ceil(totalEntries / batchSize);

      if (numBatches > 1) {
        setShowBatchButtons(true);
        setBatchInfo({
          totalEntries,
          batchSize,
          numBatches
        });
      } else {
        setShowBatchButtons(false);
        setBatchInfo(null);
      }
    } else {
      setShowBatchButtons(false);
      setBatchInfo(null);
    }

    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async (password, batchPage = null) => {
    setExportLoading(true);
    setDownloadingBatch(batchPage); // Track which batch is downloading
    setExportError("");

    try {
      const params = new URLSearchParams();

      if (exportType === "retailers") {
        if (retailersSearch) params.append("search", retailersSearch);
        if (retailersStartDate) params.append("startDate", retailersStartDate);
        if (retailersEndDate) params.append("endDate", retailersEndDate);
      } else if (exportType === "consumers") {
        if (consumersSearch) params.append("search", consumersSearch);
        if (consumersStartDate) params.append("startDate", consumersStartDate);
        if (consumersEndDate) params.append("endDate", consumersEndDate);

        // Add batch download parameters if page is specified
        if (batchPage !== null) {
          params.append("page", batchPage);
          params.append("pageSize", "100000");
        }
      }

      params.append("password", password);

      const endpoint = exportType === "retailers" ? "retailers/csv" : "consumers/csv";
      const url = `${API_URL}${endpoint}?${params}`;

      const response = await fetch(url, {
        method: "GET",
      });

      // Check if response is successful
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Invalid password or export failed");
      }

      // Check if response is CSV or JSON error
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const jsonData = await response.json();
        if (jsonData.status === "error" || jsonData.error) {
          throw new Error(jsonData.message || jsonData.error || "Export failed");
        }
      }

      // Download the file
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      const batchSuffix = batchPage !== null ? `_batch_${batchPage}` : "";
      link.download = `${exportType}_export${batchSuffix}_${new Date().getTime()}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      // Close modal on success only if not batch download
      if (!showBatchButtons) {
        setShowPasswordModal(false);
        setExportError("");
        setShowBatchButtons(false);
        setBatchInfo(null);
      } else {
        // Clear error for batch downloads, but keep modal open
        setExportError("");
      }
    } catch (error) {
      setExportError(error.message || "Failed to export. Please try again.");
    } finally {
      setExportLoading(false);
      setDownloadingBatch(null); // Clear downloading batch
    }
  };

  const exportRetailersCSV = () => {
    openExportModal("retailers");
  };

  const exportConsumersCSV = () => {
    openExportModal("consumers");
  };

  const fetchScreenCounts = async () => {
    try {
      setScreenCountsLoading(true);

      // Build query params (like ?startDate=2025-07-18&endDate=2025-07-31)
      const params = new URLSearchParams();
      if (screenStartDate) params.append("startDate", screenStartDate);
      if (screenEndDate) params.append("endDate", screenEndDate);

      const url = `${API_URL}consumers/screens/count?${params.toString()}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.status === "success") {
        setScreenCounts(data.data || []);
      } else {
        setScreenCounts([]);
      }
    } catch (error) {
      console.error("Error fetching screen counts:", error);
      setScreenCounts([]);
    } finally {
      setScreenCountsLoading(false);
    }
  };

  // --- Auto Fetch ---
  useEffect(() => {
    if (activeTab === "consumers") {
      fetchScreenCounts();
    }
  }, [activeTab, screenStartDate, screenEndDate]);

  useEffect(() => {
    fetchCashbacks();
  }, [cashbacksPage]);

  const fetchCashbacks = async () => {
    try {
      const response = await fetch(
        `${API_URL}retailers/cashbacks?page=${cashbacksPage}&pageSize=${cashbacksPageSize}`
      );
      const json = await response.json();

      if (response.ok) {
        setCashbacks(json?.data?.data || []);
        setCashbacksTotalPages(json?.data?.meta?.totalPages || 1);
      } else {
        console.error("Error fetching cashbacks:", json.message);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    }
  };

  // if (loading) return <p style={{ color: "white" }}>Loading...</p>;

  const totalRetailersPages = Math.ceil(retailersTotal / pageSize);
  const totalConsumersPages = Math.ceil(consumersTotal / pageSize);

  const thStyle = {
    padding: "12px",
    textAlign: "left",
    color: "white",
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* 🔒 Password Modal */}
      <PasswordModal
        isOpen={showPasswordModal}
        onClose={() => {
          setShowPasswordModal(false);
          setExportError("");
          setShowBatchButtons(false);
          setBatchInfo(null);
          setDownloadingBatch(null);
        }}
        onSubmit={handlePasswordSubmit}
        loading={exportLoading}
        error={exportError}
        exportType={exportType.charAt(0).toUpperCase() + exportType.slice(1)}
        showBatchButtons={showBatchButtons}
        batchInfo={batchInfo}
        downloadingBatch={downloadingBatch}
      />

      <div className="admin-dashboard">
        <div style={{ padding: "20px", maxWidth: "1400px", margin: "0 auto" }}>
          <div className="tabs-wrap">
            <div
              onClick={() => setActiveTab("retailers")}
              className={`tab ${activeTab === "retailers" ? "active" : ""}`}
            >
              RETAILERS
            </div>
            <div
              onClick={() => setActiveTab("consumers")}
              className={`tab ${activeTab === "consumers" ? "active" : ""}`}
            >
              CONSUMERS
            </div>
          </div>

          {activeTab === "retailers" && (
            <div>
              <div
                style={{
                  marginBottom: "20px",
                  fontWeight: "bold",
                }}
                className="wrapper-top-titles"
              >
                <div
                  style={{
                    boxShadow: "0 0 3px rgba(0,0,0,0.1)",
                    display: "inline-block",
                    minWidth: "60px",
                    textAlign: "center",
                  }}
                  className="title-retailers"
                >
                  Total Retailers :{" "}
                  <span className="white-box">{Number(retailerCounts?.retailerCount) || "-"}</span>
                </div>

                <div
                  style={{
                    boxShadow: "0 0 3px rgba(0,0,0,0.1)",
                    display: "inline-block",
                    minWidth: "60px",
                    textAlign: "center",
                  }}
                  className="title-retailers"
                >
                  <span>
                    Top 250 Cashback Count :
                    <span className="white-box">{Number(retailerCashbacks?.top250) || "-"}</span>
                  </span>
                </div>

                <div
                  style={{
                    boxShadow: "0 0 3px rgba(0,0,0,0.1)",
                    display: "inline-block",
                    minWidth: "60px",
                    textAlign: "center",
                  }}
                  className="title-retailers"
                >
                  <span>
                    Rs 5 Cashback Count :
                    <span className="white-box">{Number(retailerCashbacks?.notTop250) || "-"}</span>
                  </span>
                </div>

                <div
                  style={{
                    boxShadow: "0 0 3px rgba(0,0,0,0.1)",
                    display: "inline-block",
                    minWidth: "60px",
                    textAlign: "center",
                  }}
                  className="title-retailers"
                >
                  Consumers Entries :{" "}
                  <span className="white-box">{Number(retailerCounts?.totalEntries) || "-"}</span>
                </div>

                <div
                  style={{
                    boxShadow: "0 0 3px rgba(0,0,0,0.1)",
                    display: "inline-block",
                    minWidth: "60px",
                    textAlign: "center",
                  }}
                  className="title-retailers"
                >
                  <span>
                    Top 250 Cashback Amount :
                    <span className="white-box">
                      {Number(retailerCashbacks?.top250 * 1000) || "-"}
                    </span>
                  </span>
                </div>

                <div
                  style={{
                    boxShadow: "0 0 3px rgba(0,0,0,0.1)",
                    display: "inline-block",
                    minWidth: "60px",
                    textAlign: "center",
                  }}
                  className="title-retailers"
                >
                  <span>
                    Rs 5 Cashback Amount :
                    <span className="white-box">
                      {Number(retailerCashbacks?.notTop250Amount) || "-"}
                    </span>
                  </span>
                </div>
              </div>

              <div
                style={{
                  marginBottom: "20px",
                  display: "flex",
                  gap: "10px",
                  flexWrap: "wrap",
                  alignItems: "end",
                }}
              >
                <div style={{ flex: 1, position: "relative" }}>
                  <label
                    style={{ display: "block", marginBottom: "5px", fontSize: "14px" }}
                  ></label>
                  <input
                    type="text"
                    value={retailersSearch}
                    onChange={(e) => setRetailersSearch(e.target.value)}
                    placeholder="Search retailers..."
                    style={{
                      padding: "8px 32px 8px 8px",
                      width: "100%",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      marginBottom: 0,
                      height: 36,
                    }}
                  />
                  {retailersSearch && (
                    <ResetIconBtn
                      onClick={() => setRetailersSearch("")}
                      size={16}
                      topOffset="0px"
                    />
                  )}
                </div>
                <div style={{ position: "relative" }}>
                  <label style={{ display: "block", marginBottom: "5px", fontSize: "14px" }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={retailersStartDate}
                    onChange={(e) => setRetailersStartDate(e.target.value)}
                    max="2025-12-10"
                    style={{
                      padding: "8px 32px 8px 8px",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                    }}
                  />
                  {retailersStartDate && (
                    <ResetIconBtn
                      onClick={() => setRetailersStartDate("")}
                      size={16}
                      topOffset="12px"
                    />
                  )}
                </div>
                <div style={{ position: "relative" }}>
                  <label style={{ display: "block", marginBottom: "5px", fontSize: "14px" }}>
                    End Date
                  </label>
                  <input
                    type="date"
                    value={retailersEndDate}
                    onChange={(e) => setRetailersEndDate(e.target.value)}
                    max="2025-12-10"
                    style={{
                      padding: "8px 32px 8px 8px",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                    }}
                  />
                  {retailersEndDate && (
                    <ResetIconBtn
                      onClick={() => setRetailersEndDate("")}
                      size={16}
                      topOffset="12px"
                    />
                  )}
                </div>
                <div>
                  <button onClick={exportRetailersCSV} className="export-btn">
                    Export
                  </button>
                </div>
              </div>

              {retailersLoading ? (
                <p>Loading retailers...</p>
              ) : (
                <>
                  <div style={{ overflowX: "auto" }} className="bg-main-table">
                    <table
                      className="admin-table"
                      style={{ width: "100%", borderCollapse: "collapse", color: "white" }}
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              color: "white",
                            }}
                          >
                            ID
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              color: "white",
                            }}
                          >
                            Full Name
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              color: "white",
                            }}
                          >
                            Mobile Number
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              color: "white",
                            }}
                          >
                            Retailer Code
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              color: "white",
                            }}
                          >
                            TL Code
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              cursor: "pointer",
                              userSelect: "none",
                            }}
                            onClick={() => handleSort("consumerCount")}
                          >
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                color: "white",
                              }}
                            >
                              Total Entries
                              {retailersSortBy === "consumerCount" ? (
                                <span
                                  style={{
                                    fontSize: "18px",
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {retailersSortOrder === "asc" ? "▲" : "▼"}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    fontSize: "18px",
                                    color: "#bbb",
                                  }}
                                >
                                  ▼
                                </span>
                              )}
                            </span>
                          </th>

                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              cursor: "pointer",
                              userSelect: "none",
                              color: "white",
                            }}
                            onClick={() => handleSort("updatedDate")}
                          >
                            <span
                              style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
                            >
                              Registration Date
                              {retailersSortBy === "updatedDate" ? (
                                <span
                                  style={{
                                    fontSize: "20px",
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {retailersSortOrder === "asc" ? "▲" : "▼"}
                                </span>
                              ) : (
                                <span
                                  style={{
                                    fontSize: "20px",
                                    color: "#bbb",
                                  }}
                                >
                                  ▼
                                </span>
                              )}
                            </span>
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              color: "white",
                            }}
                          >
                            Registration Time
                          </th>

                          {/* Week Columns - Manually set to 4 weeks */}
                          {(() => {
                            const totalWeeks = 4;

                            return Array.from({ length: totalWeeks }, (_, weekIndex) => (
                              <th
                                key={`week-header-${weekIndex}`}
                                style={{
                                  padding: "12px",
                                  textAlign: "left",
                                  color: "white",
                                  minWidth: "150px",
                                }}
                              >
                                Week {weekIndex + 1}
                              </th>
                            ));
                          })()}
                        </tr>
                      </thead>
                      <tbody>
                        {retailers?.length > 0 ? (
                          retailers.map((r, index) => {
                            const dateObj = new Date(r.updatedDate);
                            const formattedDate = dateObj.toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            });
                            const formattedTime = dateObj.toLocaleTimeString("en-IN", {
                              hour: "2-digit",
                              minute: "2-digit",
                            });

                            return (
                              <tr key={r._id}>
                                <td style={{ padding: "12px" }}>
                                  {(retailersPage - 1) * pageSize + index + 1}
                                </td>
                                <td style={{ padding: "12px" }}>{r.fullName}</td>
                                <td style={{ padding: "12px" }}>
                                  {maskFirstSeven(r.mobileNumber)}
                                </td>
                                <td style={{ padding: "12px" }}>{r.code}</td>
                                <td style={{ padding: "12px" }}>{r.TlCode?.teamLeadCode}</td>
                                <td style={{ padding: "12px" }}>{r.consumerCount || 0}</td>
                                <td style={{ padding: "12px" }}>{formattedDate}</td>
                                <td style={{ padding: "12px" }}>{formattedTime}</td>

                                {/* Week Data - Manually set to 4 weeks */}
                                {(() => {
                                  const totalWeeks = 4;

                                  // Payout dates for each week
                                  const payoutDates = [
                                    new Date("2025-10-27"), // Week 1
                                    new Date("2025-11-03"), // Week 2
                                    new Date("2025-11-12"), // Week 3
                                    new Date("2025-11-26"), // Week 4
                                  ];

                                  // Helper to determine week from createdAt date
                                  const getWeekFromDate = (dateStr) => {
                                    const date = new Date(dateStr);
                                    // Check which payout date this falls into (within 5 days of payout)
                                    for (let i = payoutDates.length - 1; i >= 0; i--) {
                                      const payoutDate = payoutDates[i];
                                      const diffDays = (date - payoutDate) / (1000 * 60 * 60 * 24);
                                      // If createdAt is within -2 to +5 days of payout date, it belongs to that week
                                      if (diffDays >= -2 && diffDays <= 5) {
                                        return i + 1; // Week number (1-4)
                                      }
                                    }
                                    return null;
                                  };

                                  return Array.from({ length: totalWeeks }, (_, weekIndex) => {
                                    // Find weekData by matching calculated week number from createdAt
                                    const weekNum = weekIndex + 1;
                                    const weekData = r.weeklyWins?.find(w => getWeekFromDate(w.createdAt) === weekNum);

                                    return (
                                      <td
                                        key={`week-${weekIndex}`}
                                        style={{
                                          padding: "12px",
                                          verticalAlign: "top",
                                        }}
                                      >
                                        {weekData ? (
                                          <div style={{ fontSize: "13px", lineHeight: "1.6" }}>
                                            {/* <div>
                                              <strong>Rank:</strong> {weekData.rank}
                                            </div>
                                            <div>
                                              <strong>Entries:</strong> {weekData.entries}
                                            </div> */}
                                            <div>
                                              <strong>Cashback:</strong>{" "}
                                              <span
                                                style={{
                                                }}
                                              >
                                                {weekData.cashgramStatus === "CASHGRAM_REDEEMED"
                                                  ? "Claimed"
                                                  : weekData.cashgramStatus
                                                    ? weekData.cashgramStatus.replace("CASHGRAM_", "")
                                                    : "Pending"}
                                              </span>
                                            </div>
                                            {/* {weekData.totalAmount && (
                                              <div>
                                                <strong>Amount:</strong> ₹{weekData.totalAmount}
                                              </div>
                                            )} */}
                                          </div>
                                        ) : (
                                          <div

                                          >
                                            NA
                                          </div>
                                        )}
                                      </td>
                                    );
                                  });
                                })()}
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan="8"
                              style={{ padding: "20px", textAlign: "center" }}
                            >
                              No retailers found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="pagination">
                    <div className="pagination__controls">
                      <a
                        onClick={() => setRetailersPage(retailersPage - 1)}
                        disabled={retailersPage === 1}
                        className={`pagination__button ${retailersPage === 1 ? "pagination__button--disabled" : ""
                          }`}
                      >
                        Previous
                      </a>
                      <span className="pagination__info">
                        Page {retailersPage} of {totalRetailersPages || 1}
                      </span>
                      <a
                        onClick={() => setRetailersPage(retailersPage + 1)}
                        disabled={retailersPage >= totalRetailersPages}
                        className={`pagination__button ${retailersPage >= totalRetailersPages ? "pagination__button--disabled" : ""
                          }`}
                      >
                        Next
                      </a>
                    </div>
                  </div>
                  <hr
                    style={{
                      border: "none",
                      height: "2px",
                      background: "rgb(236, 221, 175)",
                      margin: "20px 0",
                    }}
                  />

                  <div style={{ color: "white" }}>
                    <h2
                      style={{
                        marginBottom: "20px",
                        color: "white",
                        fontSize: "20px",
                        fontWeight: "bold",
                        marginTop: "20px",
                      }}
                    >
                      Retailer Cashback Details
                    </h2>
                    <div
                      style={{
                        width: "100%",
                        background:
                          "linear-gradient(130deg, rgb(174, 138, 40) 0%, rgb(200, 153, 39) 100%)",
                        border: "2px solid #ecddaf",
                        borderRadius: "10px",
                        padding: "10px",
                      }}
                    >
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <th style={thStyle}>Retailer Name</th>
                            <th style={thStyle}>Mobile Number</th>
                            <th style={thStyle}>Top 250 Winner</th>
                            <th style={thStyle}>Rs 5 Cashback</th>
                            <th style={thStyle}>Total Cashback</th>
                            <th style={thStyle}>Claim Status</th>
                            <th style={thStyle}>Cashback Date</th>
                            <th style={thStyle}>Claim Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cashbacks.length > 0 ? (
                            cashbacks.map((item) => (
                              <tr key={item._id}>
                                <td style={{ padding: "12px" }}>
                                  {item?.retailerCode?.fullName || "-"}
                                </td>
                                <td style={{ padding: "12px" }}>
                                  {item?.retailerCode?.mobileNumber
                                    ? "xxxxxx" + item.retailerCode.mobileNumber.slice(-4)
                                    : "-"}{" "}
                                </td>
                                <td style={{ padding: "12px" }}>{item?.top250 ? "Yes" : "No"}</td>
                                <td style={{ padding: "12px" }}>{item?.rs5Amount || 0}</td>
                                <td style={{ padding: "12px" }}>{item?.totalAmount || 0}</td>
                                <td style={{ padding: "12px" }}>
                                  {item?.cashgramStatus === "CASHGRAM_REDEEMED"
                                    ? "Claimed"
                                    : item?.cashgramStatus === "CASHGRAM_PENDING"
                                      ? "Pending"
                                      : "-"}
                                </td>
                                <td style={{ padding: "12px" }}>
                                  {item?.createdAt
                                    ? new Date(item.createdAt).toLocaleDateString()
                                    : "-"}
                                </td>
                                <td style={{ padding: "12px" }}>
                                  {item?.updatedAt
                                    ? new Date(item.updatedAt).toLocaleDateString()
                                    : "-"}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="8" style={{ textAlign: "center", padding: "10px" }}>
                                No cashback data found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="pagination">
                    <div className="pagination__controls">
                      <a
                        onClick={() => {
                          if (cashbacksPage > 1) setCashbacksPage(cashbacksPage - 1);
                        }}
                        className={`pagination__button ${cashbacksPage === 1 ? "pagination__button--disabled" : ""
                          }`}
                      >
                        Previous
                      </a>

                      <span className="pagination__info">
                        Page {cashbacksPage} of {cashbacksTotalPages || 1}
                      </span>

                      <a
                        onClick={() => {
                          if (cashbacksPage < cashbacksTotalPages)
                            setCashbacksPage(cashbacksPage + 1);
                        }}
                        className={`pagination__button ${cashbacksPage >= cashbacksTotalPages ? "pagination__button--disabled" : ""
                          }`}
                      >
                        Next
                      </a>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === "consumers" && (
            <div>
              <div className="total-entries">
                <div
                  style={{
                    padding: "20px 10px",
                    boxShadow: "0 0 3px rgba(0,0,0,0.1)",
                    display: "inline-block",
                    minWidth: "60px",
                    textAlign: "center",
                  }}
                  className="title-retailers"
                >
                  Total Entries :{" "}
                  <span className="white-box">{Number(consumerCounts?.count) || "-"}</span>
                </div>
                <div
                  style={{
                    padding: "20px 10px",
                    boxShadow: "0 0 3px rgba(0,0,0,0.1)",
                    display: "inline-block",
                    minWidth: "60px",
                    textAlign: "center",
                  }}
                  className="title-retailers"
                >
                  Claimed Cashback Count :
                  <span className="white-box"> {Number(consumerCashbacks?.count) || "-"}</span>
                </div>
                <div
                  style={{
                    padding: "20px 10px",
                    boxShadow: "0 0 3px rgba(0,0,0,0.1)",
                    display: "inline-block",
                    minWidth: "60px",
                    textAlign: "center",
                  }}
                  className="title-retailers"
                >
                  Claimed Cashback Amount :
                  <span className="white-box">{Number(consumerCashbacks?.cashbackAmount)} </span>
                </div>
              </div>
              <div
                style={{
                  marginBottom: "20px",
                  display: "flex",
                  gap: "10px",
                  flexWrap: "wrap",
                  alignItems: "end",
                }}
              >
                <div style={{ flex: 1, position: "relative" }}>
                  <label
                    style={{ display: "block", marginBottom: "5px", fontSize: "14px" }}
                  ></label>
                  <input
                    type="text"
                    value={consumersSearch}
                    onChange={(e) => setConsumersSearch(e.target.value)}
                    placeholder="Search consumers..."
                    style={{
                      padding: "8px 32px 8px 8px",
                      width: "100%",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      marginBottom: 0,
                      height: 36,
                    }}
                  />
                  {consumersSearch && (
                    <ResetIconBtn
                      onClick={() => setConsumersSearch("")}
                      size={16}
                      topOffset="0px"
                    />
                  )}
                </div>
                <div style={{ position: "relative" }}>
                  <label style={{ display: "block", marginBottom: "5px", fontSize: "14px" }}>
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={consumersStartDate}
                    onChange={(e) => setConsumersStartDate(e.target.value)}
                    max="2025-11-30"
                    style={{
                      padding: "8px 32px 8px 8px",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                    }}
                  />
                  {consumersStartDate && (
                    <ResetIconBtn
                      onClick={() => setConsumersStartDate("")}
                      size={16}
                      topOffset="12px"
                    />
                  )}
                </div>
                <div style={{ position: "relative" }}>
                  <label style={{ display: "block", marginBottom: "5px", fontSize: "14px" }}>
                    End Date
                  </label>
                  <input
                    type="date"
                    value={consumersEndDate}
                    onChange={(e) => setConsumersEndDate(e.target.value)}
                    max="2025-11-30"
                    style={{
                      padding: "8px 32px 8px 8px",
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                    }}
                  />
                  {consumersEndDate && (
                    <ResetIconBtn
                      onClick={() => setConsumersEndDate("")}
                      size={16}
                      topOffset="12px"
                    />
                  )}
                </div>
                <div>
                  <button onClick={exportConsumersCSV} className="export-btn">
                    Export
                  </button>
                </div>
              </div>

              {consumersLoading ? (
                <p>Loading consumers...</p>
              ) : (
                <>
                  <div style={{ overflowX: "auto" }} className="bg-main-table">
                    <table
                      className="admin-table"
                      style={{ width: "100%", borderCollapse: "collapse", color: "white" }}
                    >
                      <thead style={{ color: "white" }}>
                        <tr>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                            }}
                          >
                            ID
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                            }}
                          >
                            Full Name
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                            }}
                          >
                            Mobile Number
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                            }}
                          >
                            Unique Code
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                            }}
                          >
                            Retailer Code
                          </th>

                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                            }}
                          >
                            Pincode
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                            }}
                          >
                            Age
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                            }}
                          >
                            Status
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                            }}
                          >
                            Updated Date
                          </th>
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                            }}
                          >
                            Updated Time
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {consumers?.length > 0 ? (
                          consumers.map((c, index) => {
                            const dateObj = new Date(c.updatedDate);
                            const formattedDate = dateObj.toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            });
                            const formattedTime = dateObj.toLocaleTimeString("en-IN", {
                              hour: "2-digit",
                              minute: "2-digit",
                            });

                            return (
                              <tr key={c._id}>
                                <td style={{ padding: "12px" }}>
                                  {(consumersPage - 1) * pageSize + index + 1}
                                </td>
                                <td style={{ padding: "12px" }}>{c.fullName}</td>
                                <td style={{ padding: "12px" }}>
                                  {maskFirstSeven(c.mobileNumber)}
                                </td>
                                <td style={{ padding: "12px" }}>{c.code}</td>
                                <td style={{ padding: "12px" }}>{c.retailerCode?.code}</td>
                                <td style={{ padding: "12px" }}>{c.pincode}</td>
                                <td style={{ padding: "12px" }}>{c.age}</td>
                                <td style={{ padding: "12px" }}>
                                  {c.cashgramStatus === "CASHGRAM_REDEEMED" ? (
                                    <span>Claimed</span>
                                  ) : c.cashgramStatus === "CASHGRAM_PENDING" ? (
                                    <span>Pending</span>
                                  ) : (
                                    <span>-</span>
                                  )}
                                </td>
                                <td style={{ padding: "12px" }}>{formattedDate}</td>
                                <td style={{ padding: "12px" }}>{formattedTime}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td
                              colSpan="10"
                              style={{
                                padding: "20px",
                                textAlign: "center",
                              }}
                            >
                              No consumers found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="pagination">
                    <div className="pagination__controls">
                      <a
                        onClick={() => setConsumersPage(consumersPage - 1)}
                        disabled={consumersPage === 1}
                        className={`pagination__button ${consumersPage === 1 ? "pagination__button--disabled" : ""
                          }`}
                      >
                        Previous
                      </a>
                      <span className="pagination__info">
                        Page {consumersPage} of {totalConsumersPages || 1}
                      </span>
                      <a
                        onClick={() => setConsumersPage(consumersPage + 1)}
                        disabled={consumersPage >= totalConsumersPages}
                        className={`pagination__button ${consumersPage >= totalConsumersPages ? "pagination__button--disabled" : ""
                          }`}
                      >
                        Next
                      </a>
                    </div>
                  </div>

                  <hr style={{ marginTop: 20, marginBottom: 20 }} />

                  <div style={{ marginTop: "40px" }}>
                    <div style={{ marginBottom: 20 }}>
                      <h3
                        style={{
                          color: "white",
                          marginBottom: "10px",
                          fontSize: "22px",
                          fontWeight: "bold",
                        }}
                      >
                        User Drop-offs
                      </h3>
                      <p>Count of users that dropped off after each screen in the game flow</p>
                    </div>

                    {/* Date Filters */}
                    <div
                      style={{
                        marginBottom: "20px",
                        display: "flex",
                        gap: "10px",
                        flexWrap: "wrap",
                        alignItems: "end",
                      }}
                    >
                      <div style={{ position: "relative" }}>
                        <label style={{ display: "block", marginBottom: "5px", fontSize: "14px" }}>
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={screenStartDate}
                          onChange={(e) => setScreenStartDate(e.target.value)}
                          style={{
                            padding: "8px 32px 8px 8px",
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                          }}
                        />
                        {screenStartDate && (
                          <ResetIconBtn
                            onClick={() => setScreenStartDate("")}
                            size={16}
                            topOffset="12px"
                          />
                        )}
                      </div>

                      <div style={{ position: "relative" }}>
                        <label style={{ display: "block", marginBottom: "5px", fontSize: "14px" }}>
                          End Date
                        </label>
                        <input
                          type="date"
                          value={screenEndDate}
                          onChange={(e) => setScreenEndDate(e.target.value)}
                          style={{
                            padding: "8px 32px 8px 8px",
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                          }}
                        />
                        {screenEndDate && (
                          <ResetIconBtn
                            onClick={() => setScreenEndDate("")}
                            size={16}
                            topOffset="12px"
                          />
                        )}
                      </div>
                    </div>

                    {/* Table */}
                    <div>
                      {screenCountsLoading ? (
                        <div className="screen-count-table">
                          <p>Loading screen counts...</p>
                        </div>
                      ) : (
                        <div
                          style={{
                            overflowX: "auto",
                            border: "2px solid #ECDDAF",
                            borderRadius: "10px",
                            background: "#be9426",
                          }}
                        >
                          <table
                            className="admin-table"
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              color: "white",
                            }}
                          >
                            <thead>
                              <tr>
                                <th style={{ padding: "12px", textAlign: "left" }}>Screen</th>
                                <th style={{ padding: "12px", textAlign: "center" }}>User Count</th>
                              </tr>
                            </thead>
                            <tbody>
                              {screenCounts?.length > 0 ? (
                                screenCounts?.map((item, index) => (
                                  <tr key={index}>
                                    <td style={{ padding: "12px" }}>
                                      {item.step == "verified" ? "Verified Screen" : item.step}
                                    </td>
                                    <td style={{ padding: "12px", textAlign: "center" }}>
                                      {item.count}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td colSpan="2" style={{ padding: "12px", textAlign: "center" }}>
                                    No screen count data found
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
